import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Banknote, Download, Loader2, Search, FileText, MapPin, Euro, Calendar } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const API = ((typeof window !== 'undefined' && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const fmtEur = (v) => {
  if (v == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
};

const STATUS_BADGE = {
  prospection: { label: 'Prospection', cls: 'bg-zinc-700 text-zinc-200' },
  en_cours_achat: { label: 'En cours d\'achat', cls: 'bg-amber-600/30 text-amber-200 border-amber-500/40' },
  achete: { label: 'Acheté', cls: 'bg-blue-600/30 text-blue-200 border-blue-500/40' },
  travaux: { label: 'Travaux', cls: 'bg-orange-600/30 text-orange-200 border-orange-500/40' },
  en_cours_vente: { label: 'En vente', cls: 'bg-violet-600/30 text-violet-200 border-violet-500/40' },
  vendu: { label: 'Vendu', cls: 'bg-emerald-600/30 text-emerald-200 border-emerald-500/40' },
};

export const DossierFinancementPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => { loadEligibleProjects(); }, []);

  const loadEligibleProjects = async () => {
    setLoading(true);
    try {
      // On charge projets, biens et offres en parallèle pour construire la liste.
      // Promise.allSettled : on continue même si l'un des appels échoue.
      const [projectsRes, propertiesRes, offersRes] = await Promise.allSettled([
        axios.get(`${API}/projects`, { withCredentials: true }),
        axios.get(`${API}/properties`, { withCredentials: true }),
        axios.get(`${API}/offers`, { withCredentials: true }),
      ]);
      const projects = (projectsRes.status === 'fulfilled' && Array.isArray(projectsRes.value?.data)) ? projectsRes.value.data : [];
      const properties = (propertiesRes.status === 'fulfilled' && Array.isArray(propertiesRes.value?.data)) ? propertiesRes.value.data : [];
      const offers = (offersRes.status === 'fulfilled' && Array.isArray(offersRes.value?.data)) ? offersRes.value.data : [];

      // Index par property_id pour récupérer rapidement
      const propsById = Object.fromEntries(properties.map((p) => [p?.id, p]).filter(([id]) => id));
      // On regroupe les offres par property_id pour savoir lesquels en ont au moins une
      const offersByPropId = offers.reduce((acc, o) => {
        const pid = o?.property_id;
        if (!pid) return acc;
        if (!acc[pid]) acc[pid] = [];
        acc[pid].push(o);
        return acc;
      }, {});

      // Un projet est éligible au dossier de financement dès qu'il a au moins une
      // offre d'achat sur son bien associé (étape "demande de financement")
      const eligible = projects
        .filter((proj) => proj?.property_id && (offersByPropId[proj.property_id]?.length || 0) > 0)
        .map((proj) => {
          const prop = propsById[proj.property_id] || {};
          // Copie pour ne pas muter l'array source du reducer
          const propOffers = [...(offersByPropId[proj.property_id] || [])];
          // Offre acceptée prioritaire, sinon dernière
          const accepted = propOffers.find((o) => o?.status === 'acceptee');
          const latest = propOffers.sort((a, b) => (b?.created_at || '').localeCompare(a?.created_at || ''))[0];
          const refOffer = accepted || latest;
          return {
            id: proj.id,
            project: proj,
            property: prop,
            offer: refOffer,
            offers_count: propOffers.length,
            has_accepted: !!accepted,
            updated_at: proj.updated_at || proj.created_at,
          };
        })
        .sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));

      setItems(eligible);
    } catch (e) {
      console.error('Erreur chargement dossiers de financement:', e);
      toast.error('Erreur lors du chargement des projets éligibles');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (projectId, propTitle) => {
    setDownloadingId(projectId);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/bank-dossier-pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fname = `dossier-financement-${(propTitle || 'projet').replace(/[^a-z0-9]/gi, '-').toLowerCase()}.pdf`;
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Dossier téléchargé');
    } catch (e) {
      // Avec responseType=blob, response.data est un Blob — il faut le lire en texte pour extraire le detail JSON
      let msg = 'Erreur lors de la génération du dossier';
      try {
        const blob = e.response?.data;
        if (blob && typeof blob.text === 'function') {
          const txt = await blob.text();
          try {
            const parsed = JSON.parse(txt);
            if (parsed?.detail) msg = typeof parsed.detail === 'string' ? parsed.detail : JSON.stringify(parsed.detail);
          } catch { /* pas du JSON, on garde le message générique */ }
        }
      } catch { /* fallback message */ }
      toast.error(msg);
    } finally {
      setDownloadingId(null);
    }
  };

  const filtered = items.filter((it) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const p = it.property || {};
    return [p.title, p.address, p.city, p.postal_code].some((v) => (v || '').toLowerCase().includes(q));
  });

  return (
    <div className="space-y-6" data-testid="dossier-financement-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Banknote className="w-6 h-6 text-emerald-500" />
            Dossier de financement
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Tous les projets ayant fait l'objet d'au moins une offre d'achat — clic sur un projet pour télécharger le dossier banque.
          </p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un bien…"
            className="pl-9 w-64 bg-zinc-950 border-zinc-800 text-white"
            data-testid="dossier-search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-zinc-900/40 border border-dashed border-zinc-800 rounded-xl p-12 text-center">
          <FileText className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-400 mb-1">Aucun projet en demande de financement.</p>
          <p className="text-xs text-zinc-600">Un projet apparaît ici dès qu'une offre d'achat est créée sur le bien.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((it) => {
            const p = it.property || {};
            const sb = STATUS_BADGE[it.project?.status] || { label: it.project?.status || '—', cls: 'bg-zinc-700 text-zinc-200' };
            const isDl = downloadingId === it.id;
            const refPrice = it.offer?.contre_offre || it.offer?.prix_propose || p.price;
            return (
              <button
                key={it.id}
                disabled={isDl}
                onClick={() => handleDownload(it.id, p.title)}
                className="group text-left bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 hover:border-emerald-600/50 rounded-xl p-4 transition-all hover:shadow-lg hover:shadow-emerald-900/20 disabled:opacity-50 disabled:cursor-wait"
                data-testid={`dossier-card-${it.id}`}
              >
                {p.images?.[0] && (
                  <div className="w-full h-32 rounded-lg overflow-hidden mb-3 bg-zinc-800">
                    <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-white line-clamp-2 flex-1">
                    {p.title || '(Sans titre)'}
                  </h3>
                  <Badge className={`${sb.cls} text-[10px] flex-shrink-0`}>{sb.label}</Badge>
                </div>
                <p className="text-xs text-zinc-500 flex items-center gap-1 mb-2 truncate">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  {[p.address, p.postal_code, p.city].filter(Boolean).join(', ') || '—'}
                </p>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div>
                    <p className="text-zinc-600 uppercase tracking-wider">Prix réf.</p>
                    <p className="text-amber-300 font-mono font-semibold flex items-center gap-1">
                      <Euro className="w-3 h-3" />{fmtEur(refPrice)}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-600 uppercase tracking-wider">Offres</p>
                    <p className="text-zinc-300 font-semibold">
                      {it.offers_count} {it.has_accepted && <span className="text-emerald-400">✓ acceptée</span>}
                    </p>
                  </div>
                </div>
                {it.updated_at && (
                  <p className="text-[10px] text-zinc-600 mt-3 flex items-center gap-1">
                    <Calendar className="w-2.5 h-2.5" />MAJ {new Date(it.updated_at).toLocaleDateString('fr-FR')}
                  </p>
                )}
                <div className="mt-3 pt-3 border-t border-zinc-800/60 flex items-center justify-between">
                  <span className="text-xs text-zinc-500 group-hover:text-emerald-400 transition-colors">
                    {isDl ? 'Génération en cours…' : 'Télécharger le dossier'}
                  </span>
                  {isDl ? (
                    <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 text-zinc-600 group-hover:text-emerald-400 transition-colors" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DossierFinancementPage;
